import React, { useState, useEffect } from 'react';

const SECTIONS = [
  {
    id: 1, title: 'Ad Account Access',
    items: [
      { id: 'a1', title: 'Google Ads account access obtained', note: 'Campaigns already set up and running. Manager-level access confirmed.', initial: 'done' },
      { id: 'a2', title: 'LSA (Local Services Ads) account access', note: 'Needs verification — confirm we have been granted access and can manage bids/budget.', initial: 'review' },
      { id: 'a3', title: 'Google Analytics / GA4 access', note: 'Confirm GA4 property exists. Grant editor access to our agency account.', initial: 'todo' },
      { id: 'a4', title: 'Google Search Console access', note: 'Verify domain ownership is confirmed. Add our account as owner/full user.', initial: 'todo' },
      { id: 'a5', title: 'Google Business Profile (GBP) access', note: 'Need manager access to the GBP listing for local SEO management.', initial: 'todo' },
      { id: 'a6', title: 'Meta (Facebook/Instagram) Business Manager access', note: 'Add our agency as a partner in Business Manager with ad account and page permissions.', initial: 'todo' },
    ]
  },
  {
    id: 2, title: 'PPC & Campaign Audit',
    items: [
      { id: 'b1', title: 'Audit existing Google Ads campaigns from Perry Marketing', note: 'Review campaign structure, ad copy, targeting, bidding strategy, and spend history.', initial: 'priority' },
      { id: 'b2', title: 'Document all current landing page URLs (Perry Marketing hosted)', note: 'Identify every external landing page in active campaigns — on domains NOT owned by the client.', initial: 'priority' },
      { id: 'b3', title: 'Map campaigns → landing pages → conversion tracking', note: 'Understand the full funnel for each active campaign before any changes are made.', initial: 'todo' },
      { id: 'b4', title: 'Confirm Perry Marketing transition plan & handoff timeline', note: 'Establish what Perry will hand off, what they retain, and the official end date of their management.', initial: 'todo' },
    ]
  },
  {
    id: 3, title: 'Website & Domain Ownership',
    items: [
      { id: 'c1', title: "Confirm client owns their primary domain", note: "Verify registrar login, renewal date, and that DNS is in client's control — not Perry's.", initial: 'priority' },
      { id: 'c2', title: 'Get hosting & CMS credentials for main website', note: "WordPress, Webflow, etc. — backend access needed to build landing pages on client's own domain.", initial: 'todo' },
      { id: 'c3', title: "Plan & build internal landing pages on client's own domain", note: "Replace Perry's externally-hosted landing pages. Client owns all content and IP.", initial: 'todo' },
      { id: 'c4', title: 'Set up call tracking & form conversion tracking on internal pages', note: 'Tracking must be rebuilt and verified on new pages before campaigns are redirected.', initial: 'todo' },
      { id: 'c5', title: 'Migrate ad campaigns to internal landing pages', note: 'Only after new pages are live, tested, and tracking confirmed. Staged rollout recommended.', initial: 'todo' },
    ]
  },
  {
    id: 4, title: 'SEO Foundation',
    items: [
      { id: 'd1', title: 'Initial SEO audit — technical, on-page, local', note: 'Crawl errors, page speed, meta tags, schema markup, mobile friendliness.', initial: 'todo' },
      { id: 'd2', title: 'Keyword research — plumbing services + service area targeting', note: 'Identify organic target keywords and align with existing PPC ad group structure.', initial: 'todo' },
      { id: 'd3', title: 'GBP optimization — categories, services, photos, posts', note: 'Requires GBP manager access (see Ad Account Access above).', initial: 'todo' },
      { id: 'd4', title: 'Citation / NAP audit — Name, Address, Phone consistency', note: 'Check Yelp, Angi, HomeAdvisor, BBB, and major local directories.', initial: 'todo' },
    ]
  },
  {
    id: 5, title: 'Social Media',
    items: [
      { id: 'e1', title: 'Facebook page access — CONFIRMED', note: 'We have access to their Facebook page. Verify posting permissions and ad account linkage.', initial: 'done' },
      { id: 'e2', title: 'Audit existing social profiles — Instagram, TikTok, etc.', note: 'Check branding consistency, bio, contact info, and posting history across all platforms.', initial: 'todo' },
      { id: 'e3', title: 'Confirm admin access to all remaining social accounts', note: 'Need page admin or Business Manager access — not just "Editor" on individual pages.', initial: 'todo' },
      { id: 'e4', title: 'Collect video footage for content creation', note: 'Need raw video footage — job site clips, team introductions, before/after, service walk-throughs. This is required before we can start producing social content.', initial: 'priority' },
      { id: 'e5', title: 'Collect photos — jobs, team, equipment, service areas', note: 'Need image dump for social posts, website, and ads. See Assets tab to upload directly.', initial: 'priority' },
      { id: 'e6', title: 'Develop social media content strategy & content calendar', note: 'Define posting cadence, content pillars (tips, reviews, before/after, promos), and client approval workflow.', initial: 'todo' },
    ]
  },
  {
    id: 6, title: 'Brand Assets',
    items: [
      { id: 'f1', title: 'Logo files — RECEIVED', note: 'Logo files have been collected. Confirm all formats are present: SVG, PNG (transparent), and any color variations.', initial: 'done' },
      { id: 'f2', title: 'Brand color codes & fonts', note: 'Document exact hex codes and font names for consistency across all materials.', initial: 'todo' },
      { id: 'f3', title: 'Any existing brand guidelines or style guide', note: 'If available, collect any existing brand standards document.', initial: 'todo' },
    ]
  },
  {
    id: 7, title: 'Reporting & Client Setup',
    items: [
      { id: 'g1', title: 'Set up client reporting dashboard', note: 'Connect GA4, Google Ads, and GBP into a unified report (Looker Studio or similar).', initial: 'todo' },
      { id: 'g2', title: 'Define KPIs & document baseline metrics', note: 'Capture current spend, cost-per-lead, call volume, and organic rankings before our management begins.', initial: 'todo' },
      { id: 'g3', title: 'Schedule kickoff / onboarding call with client', note: 'Align on goals, timeline, communication cadence, and content approval process.', initial: 'todo' },
    ]
  },
];

const BADGE = {
  complete:  { bg: 'rgba(29,184,110,0.15)', color: '#4de09a', border: '1px solid rgba(29,184,110,0.28)', label: 'Complete' },
  verify:    { bg: 'rgba(230,168,0,0.15)',  color: '#f0c040', border: '1px solid rgba(230,168,0,0.28)',  label: 'Verify' },
  todo:      { bg: 'rgba(255,255,255,0.06)',color: '#999',    border: '1px solid rgba(255,255,255,0.12)',label: 'To Do' },
  priority:  { bg: 'rgba(214,0,0,0.18)',    color: '#ff5555', border: '1px solid rgba(214,0,0,0.35)',   label: 'Priority' },
  done:      { bg: 'rgba(29,184,110,0.15)', color: '#4de09a', border: '1px solid rgba(29,184,110,0.28)', label: 'Complete' },
  review:    { bg: 'rgba(230,168,0,0.15)',  color: '#f0c040', border: '1px solid rgba(230,168,0,0.28)',  label: 'Verify' },
};

const ITEM_STATUS = {
  done:     { bg: 'rgba(29,184,110,0.07)', border: 'rgba(29,184,110,0.22)', left: '#1db86e' },
  review:   { bg: 'rgba(230,168,0,0.07)',  border: 'rgba(230,168,0,0.22)',  left: '#e6a800' },
  todo:     { bg: '#1e1e1e', border: '#3a3a3a', left: '#3a3a3a' },
  priority: { bg: 'rgba(214,0,0,0.05)', border: 'rgba(214,0,0,0.25)', left: '#D60000' },
};

function initStatuses() {
  const s = {};
  SECTIONS.forEach(sec => sec.items.forEach(item => { s[item.id] = item.initial; }));
  return s;
}

export default function Onboarding({ onStatsChange }) {
  const [statuses, setStatuses] = useState(initStatuses);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const done = Object.values(statuses).filter(s => s === 'done').length;
    const total = Object.values(statuses).length;
    onStatsChange(done, total);
  }, [statuses, onStatsChange]);

  const toggle = (id) => {
    setStatuses(prev => {
      const cur = prev[id];
      const next = cur === 'done' ? 'review' : cur === 'review' ? 'todo' : 'done';
      return { ...prev, [id]: next };
    });
  };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 48px 60px' }}>
      {SECTIONS.map(sec => {
        const secDone = sec.items.filter(i => statuses[i.id] === 'done').length;
        return (
          <div key={sec.id} style={{ marginBottom: 30 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ width: 4, height: 26, background: '#D60000', borderRadius: 2, flexShrink: 0, marginRight: 12 }} />
              <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#fff' }}>{sec.title}</div>
              <div style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: '#aaa' }}>{secDone} / {sec.items.length} done</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {sec.items.map(item => {
                const st = statuses[item.id];
                const ist = ITEM_STATUS[st] || ITEM_STATUS.todo;
                const badge = BADGE[st] || BADGE.todo;
                const isDone = st === 'done';
                const isReview = st === 'review';
                return (
                  <div
                    key={item.id}
                    onClick={() => toggle(item.id)}
                    style={{
                      background: ist.bg, border: `1px solid ${ist.border}`,
                      borderLeft: `3px solid ${ist.left}`, borderRadius: 3,
                      padding: '13px 16px', display: 'flex', alignItems: 'flex-start',
                      gap: 13, cursor: 'pointer', transition: 'all 0.15s ease',
                    }}
                  >
                    <div style={{
                      width: 20, height: 20, border: `2px solid ${isDone ? '#1db86e' : isReview ? '#e6a800' : '#3a3a3a'}`,
                      borderRadius: 3, flexShrink: 0, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isDone ? '#1db86e' : isReview ? '#e6a800' : 'transparent', transition: 'all 0.15s ease',
                      color: '#fff', fontSize: 12, fontWeight: 700,
                    }}>
                      {isDone ? '✓' : isReview ? '!' : ''}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: isDone ? '#6fcfa2' : isReview ? '#f0c040' : '#fff', lineHeight: 1.4 }}>{item.title}</div>
                      <div style={{ fontSize: 12, color: '#aaa', marginTop: 4, lineHeight: 1.55 }}>{item.note}</div>
                    </div>
                    <span style={{
                      fontFamily: "'Barlow Condensed', sans-serif", fontSize: 10, fontWeight: 700,
                      letterSpacing: '0.1em', textTransform: 'uppercase', padding: '3px 8px',
                      borderRadius: 2, flexShrink: 0, marginTop: 2,
                      background: badge.bg, color: badge.color, border: badge.border,
                    }}>{badge.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      <div style={{ background: '#1e1e1e', border: '1px solid #3a3a3a', borderLeft: '3px solid #D60000', borderRadius: 3, padding: 20, marginTop: 30 }}>
        <h3 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 14, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#fff', marginBottom: 12 }}>Internal Notes</h3>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Add notes here — Perry Marketing handoff details, client contacts, key dates, access credential locations, etc..."
          style={{ width: '100%', background: '#111', border: '1px solid #3a3a3a', borderRadius: 3, padding: '10px 12px', fontFamily: "'Barlow', sans-serif", fontSize: 13, color: '#fff', resize: 'vertical', minHeight: 90, outline: 'none', lineHeight: 1.6 }}
        />
      </div>
    </div>
  );
}
